/**
 * Campana del CRM (FC-743). Única puerta a los endpoints `CrmBell/*`.
 *
 * Contrato de backend: `.claude/crm_notificaciones/contrato-backend.md` y las
 * dos guías que resume (FC-744 «Para hacer» y FC-747 «Novedades/Seguimiento»).
 *
 * Tres cosas que conviene saber antes de tocar esto:
 *
 * 1. Los tres grupos llegan YA ORDENADOS y con los textos (`title`, `detail`,
 *    `time_label`) redactados en español por backend. El front NO reordena ni
 *    recompone: pinta. `data` trae los crudos por si alguna tarjeta necesita
 *    armar algo distinto.
 * 2. No hay paginación: las ventanas son cortas (24 h y +2 días en Para hacer,
 *    7 días en Novedades, 14 días en las tareas vencidas).
 * 3. Un aviso no se «lee»: se apaga cuando desaparece su causa (se tipificó la
 *    llamada, se registró la acción, se cumplió la tarea o pasó la ventana).
 *    Por eso aquí no hay nada parecido a `mark_as_read`.
 */
import { apiClient } from "@/api/softphone_api";

const BASE = "api/fxCrm/CrmBell/";

/** Respuesta vacía con la forma de un grupo, para los catch y el estado inicial. */
export const grupoVacio = () => ({ items: [], total: 0, urgent: 0 });

/** Respuesta vacía de Seguimiento, que tiene dos bloques en vez de uno. */
export const seguimientoVacio = () => ({
  stalled: { items: [], total: 0 },
  overdue_tasks: { items: [], total: 0 },
});

/**
 * Grupo «Para hacer»: citas, citas terminadas, llamadas sin tipificar y tareas.
 * De aquí sale también el contador de la campana (`total` / `urgent`).
 * @returns {Promise<{items: Array, total: number, urgent: number}>}
 */
export async function fetchToDo() {
  const { data } = await apiClient.get(`${BASE}to_do/`);
  return normalizarGrupo(data);
}

/**
 * Grupo «Novedades»: correos recibidos y las cuatro caras de una aprobación.
 * Ventana de 7 días.
 * @returns {Promise<{items: Array, total: number, urgent: number}>}
 */
export async function fetchNews() {
  const { data } = await apiClient.get(`${BASE}news/`);
  return normalizarGrupo(data);
}

/**
 * Grupo «Seguimiento»: interacciones estancadas + el bloque plegable de tareas
 * vencidas hace más de 14 días.
 *
 * OJO con los dos totales: `overdue_tasks.total` cuenta TODAS las vencidas,
 * mientras que `items` trae como mucho 30 (el tope del bloque). El encabezado
 * usa el total, no la longitud de la lista.
 * @returns {Promise<{stalled: object, overdue_tasks: object}>}
 */
export async function fetchFollowUp() {
  const { data } = await apiClient.get(`${BASE}follow_up/`);
  const vacio = seguimientoVacio();
  return {
    stalled: normalizarBloque(data?.stalled, vacio.stalled),
    overdue_tasks: normalizarBloque(data?.overdue_tasks, vacio.overdue_tasks),
  };
}

/**
 * «✓ Cumplir»: marca el evento de agenda como realizado (`status_event`).
 *
 * Recuerda que una «tarea» es un evento de agenda, no una entidad aparte, así
 * que el id que va aquí es `data.agenda_id` del aviso, NO el `source.id` de
 * cualquier otro tipo. Es idempotente: si ya estaba cumplida responde 200.
 *
 * Devuelve `item_key: "task:<id>"`, pero en «Para hacer» el mismo evento puede
 * estar pintado como `appointment:<id>` o `appointment_ended:<id>`. Por eso
 * quien llame debe retirar el aviso por `source` (model `Agenda` + ese id), no
 * comparando `key` a ciegas.
 *
 * @param {number|string} agendaId - `data.agenda_id` del aviso.
 * @returns {Promise<{id: number, status_event: boolean, item_key: string}>}
 */
export async function completeTask(agendaId) {
  const { data } = await apiClient.post(`${BASE}tasks/${agendaId}/complete/`);
  return data;
}

/** El 404 de «Cumplir» no es un error de verdad: el aviso ya no aplica. */
export const esTareaDesaparecida = (error) => error?.response?.status === 404;

const normalizarGrupo = (data) => ({
  items: Array.isArray(data?.items) ? data.items : [],
  total: Number(data?.total) || 0,
  urgent: Number(data?.urgent) || 0,
});

const normalizarBloque = (bloque, vacio) =>
  bloque
    ? {
        items: Array.isArray(bloque.items) ? bloque.items : [],
        total: Number(bloque.total) || 0,
      }
    : vacio;
